const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://dev-api.psm.knowbe4.com';
const API_VERSION = 1;
const API_URL = `${API_BASE_URL}/api/v${API_VERSION}`;

const DMI_API_URL = process.env.VUE_APP_DMI_API_URL;

const querystring = params => Object.keys(params)
  .map(key => params[key] ? key + '=' + encodeURIComponent(params[key]) : '').join('&');

export const getClients = () => {
  return request(`${API_URL}/clients`);
};

export const getPermissions = () => {
  const url = `${API_URL}/fe/permissions`;
  return request(url);
};

// Emails APIs
export const getEmails = async (type, value, params) => {
  // Create separate parameter objects for each API to avoid cross-contamination
  const psmParams = { ...params };
  if (params.dmiOffset) delete psmParams.dmiOffset;

  const dmiParams = { ...params };
  if (params.psmOffset) delete dmiParams.psmOffset;

  // Make separate API calls with appropriate params
  const emailsFromPSM = await getEmailsFromPSM(type, value, psmParams);
  const emailsFromDMI = await getEmailsFromDMI(value, dmiParams);

  if (!emailsFromPSM.results.length && !emailsFromDMI.results.length) {
    throw new Error('No emails found for recipient');
  }

  const results = [
    ...emailsFromPSM.results.map(e => {
      e.sendingSystem = 'psm';
      return e;
    }),
    ...emailsFromDMI.results.map(e => {
      e.sendingSystem = 'dmi';
      return e;
    })
  ].sort((a, b) => new Date(b.status_updated_at) - new Date(a.status_updated_at));

  return {
    results,
    psmOffset: emailsFromPSM.offset,
    dmiOffset: emailsFromDMI.offset
  };
};

const getEmailsFromPSM = (type, value, params) => {
  // Create a clean params object specifically for PSM API
  const cleanParams = {};

  // Copy only the parameters that PSM API expects
  if (params['page-size']) cleanParams['page-size'] = params['page-size'];
  if (params['api-client-id']) cleanParams['api-client-id'] = params['api-client-id'];

  // Handle cursor-based pagination: use PSM token or start from beginning
  if (params['psmOffset']) {
    // Use the PSM-provided cursor token for pagination
    cleanParams.offset = params['psmOffset'];
  }
  // If no token provided, start from the beginning (no offset parameter)

  const url = `${API_URL}/emails/${type}/${value}?${querystring(cleanParams)}`;

  return request(url)
    .then(res => {
      return res;
    })
    .catch(error => {
      console.error('PSM API error:', error, 'URL:', url);
      return { results: [], error };
    });
};

const getEmailsFromDMI = (query, params) => {
  // Skip DMI API call if DMI_API_URL is not defined
  if (!DMI_API_URL) {
    return Promise.resolve({ results: [], offset: null });
  }

  // Create a clean params object specifically for DMI API
  const cleanParams = {
    pageSize: params['page-size']
  };

  // Add API client ID if available
  if (params['api-client-id']) cleanParams.apiClientId = params['api-client-id'];

  // Handle cursor-based pagination: use DMI token or start from beginning
  if (params['dmiOffset']) {
    // Use the DMI-provided cursor token for pagination
    cleanParams.offset = params['dmiOffset'];
  }
  // If no token provided, start from the beginning (no offset parameter)

  const url = `${DMI_API_URL}/search?q=${query}&${querystring(cleanParams)}`;

  return request(url)
    .then(res => res.results && res.results.length ? res : { results: [], offset: null })
    .catch(error => {
      console.error('DMI API error:', error, 'URL:', url);
      return { results: [], offset: null, error };
    });
};


// Smarthost APIS
export const getSmarthosts = domain => {
  const url = `${API_URL}/smarthosts/${domain}`;
  return request(url);
};

export const testSmarthost = (smarthost, region = 'us') => {
  const url = `${API_URL}/smarthosts/test/${region}`;
  const config = {
    method: 'POST',
    body: JSON.stringify({
      host: smarthost.host,
      port: smarthost.port,
      requireTLS: smarthost.requireTLS,
      username: smarthost.username,
      password: smarthost.password
    })
  };

  return request(url, config);
};

export const setSmarthosts = (domain, smarthosts) => {
  const url = `${API_URL}/smarthosts/${domain}`;
  const config = {
    method: 'POST',
    body: JSON.stringify(smarthosts)
  };

  return request(url, config);
};

export const deleteSmarthosts = domain => {
  const url = `${API_URL}/smarthosts/${domain}`;
  return request(url, { method: 'DELETE' });
};

// Domain Keys APIs
export const getSelectorsForDomain = domain => {
  const url = `${API_URL}/dkim/${domain}`;
  return request(url);
};

export const createDomainSelector = domain => {
  const url = `${API_URL}/dkim/${domain}`;
  const config = {
    method: 'POST'
  };

  return request(url, config);
};

export const validateDomainSelector = (domain, selector) => {
  const url = `${API_URL}/dkim/${domain}/${selector}/validate`;
  const config = {
    method: 'PUT'
  };

  return request(url, config);
};

export const deleteDomainSelector = (domain, selector) => {
  const url = `${API_URL}/dkim/${domain}/${selector}`;
  const config = {
    method: 'DELETE'
  };

  return request(url, config);
};

// Blocked Recipients APIs
export const getBlockedRecipients = () => {
  const url = `${API_URL}/blocked`;
  return request(url);
};

export const createBlockedRecipient = (domain, blockType, comment) => {
  const url = `${API_URL}/blocked`;
  const config = {
    method: 'POST',
    body: JSON.stringify({
      domain,
      blockType,
      comment
    })
  };

  return request(url, config);
};

export const deleteBlockedRecipient = (domain) => {
  const url = `${API_URL}/blocked/${domain}`;
  const config = {
    method: 'DELETE'
  };

  return request(url, config);
};

const request = (url, options) => {
  const jwt = sessionStorage.getItem('backstage_jwt');
  const headers = {
    ...(options?.headers || {}),
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };

  return fetch(url, {
    ...options,
    headers,
    credentials: jwt ? 'omit' : 'include',
  })
    .then(res => {
      if (res.status === 401) {
        if (jwt && window.parent !== window) {
          const parentOrigin = sessionStorage.getItem('backstage_parent_origin');
          if (parentOrigin) {
            window.parent.postMessage({ type: 'AUTH_EXPIRED', source: 'iframe' }, parentOrigin);
          }
          throw new Error('Authentication expired');
        } else {
          window.location = `${API_BASE_URL}/auth`;
        }
      } else if (res.status >= 400) {
        return res.text().then(err => {
          // If the error contains pagination/offset error, provide more context
          if (err.includes('offset') || err.includes('unmarshal')) {
            console.error('Pagination error:', err, 'URL:', url);
            throw new Error('Error loading data. Please try refreshing the page.');
          } else {
            throw new Error(err);
          }
        });
      } else {
        return res;
      }
    })
    .then(res => {
      if (res.status === 204 || res.status === 201) {
        return Promise.resolve();
      } else {
        return res.json().catch(err => {
          console.error('JSON parsing error:', err);
          throw new Error('Error processing response data. Please try again.');
        });
      }
    });
};
